import React, { useEffect, useState } from "react";
import { api, rupiah, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Search, Pencil, Trash2, User } from "lucide-react";

const empty = { name: "", phone: "", email: "", address: "" };

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = () => api.get("/customers", { params: { search } }).then((r) => setCustomers(r.data)).catch(() => {});
  useEffect(() => { load(); }, [search]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...empty, ...c }); setOpen(true); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async () => {
    try {
      if (editing) await api.put(`/customers/${editing.id}`, form);
      else await api.post("/customers", form);
      toast.success("Pelanggan disimpan"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (c) => {
    try { await api.delete(`/customers/${c.id}`); toast.success("Pelanggan dihapus"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="customers-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Data Pelanggan</h1>
          <p className="text-muted-foreground text-sm">Kelola pelanggan setia toko Anda.</p>
        </div>
        <Button onClick={openNew} className="rounded-lg" data-testid="add-customer-btn"><Plus className="w-4 h-4 mr-1" /> Tambah Pelanggan</Button>
      </div>

      <div className="relative max-w-sm">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama / telepon..." className="pl-9 rounded-lg" data-testid="customer-search" />
      </div>

      <Card className="border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto kasir-scroll">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-muted-foreground">
              <tr className="text-left">
                <th className="py-3 px-4 font-medium">Nama</th>
                <th className="py-3 px-4 font-medium">Telepon</th>
                <th className="py-3 px-4 font-medium text-right">Total Belanja</th>
                <th className="py-3 px-4 font-medium text-right">Kunjungan</th>
                <th className="py-3 px-4 font-medium text-right">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`customer-row-${c.id}`}>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-accent text-primary flex items-center justify-center font-bold text-xs">{c.name?.[0]?.toUpperCase() || <User className="w-4 h-4" />}</div>
                      <span className="font-medium">{c.name}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4">{c.phone || "-"}</td>
                  <td className="py-3 px-4 text-right font-semibold">{rupiah(c.total_spent)}</td>
                  <td className="py-3 px-4 text-right">{c.visits}</td>
                  <td className="py-3 px-4 text-right">
                    <button onClick={() => openEdit(c)} className="text-slate-400 hover:text-primary p-1"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => remove(c)} className="text-slate-400 hover:text-red-600 p-1" data-testid={`delete-customer-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
              {customers.length === 0 && <tr><td colSpan={5} className="py-12 text-center text-muted-foreground">Belum ada pelanggan.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="customer-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Pelanggan" : "Tambah Pelanggan"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nama</Label><Input value={form.name} onChange={set("name")} className="mt-1 rounded-lg" data-testid="customer-name-input" /></div>
            <div><Label>Telepon</Label><Input value={form.phone} onChange={set("phone")} className="mt-1 rounded-lg" data-testid="customer-phone-input" /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={set("email")} className="mt-1 rounded-lg" /></div>
            <div><Label>Alamat</Label><Input value={form.address} onChange={set("address")} className="mt-1 rounded-lg" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-lg">Batal</Button>
            <Button onClick={save} className="rounded-lg" data-testid="save-customer-btn">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
